import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

@Injectable({ providedIn: 'root' })
export class DateUtilsService {

  /**
   * ValidatorFactory:
   * - expects "DD.MM.YYYY"
   * - allows "DD=00" (unknown day) and treats it as day 1 for calculations
   * - rejects futue dates
   */
  maxMonthsAgoValidator(maxMonths: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const raw = control.value;
      if (raw == null || raw === '') return null;

      const d = this.parseGermanDate(raw);
      if (!d) return { invalidDate: true };

      const today = new Date();
      const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const dateMidnight = new Date(d.getFullYear(), d.getMonth(), d.getDate());

      if (dateMidnight.getTime() > todayMidnight.getTime()) {
        return { invalidDate: true };
      }

      const threshold = this.subtractMonths(todayMidnight, maxMonths);

      if (dateMidnight.getTime() < threshold.getTime()) {
        return { olderThanMaxMonths: { maxMonths } };
      }

      return null;
    };
  }

  /**
   * Parses "DD.MM.YYYY" into a Date.
   * Month "00" is rejected; day "00" is treated as "01".
   */
  parseGermanDate(value: unknown): Date | null {
    if (typeof value !== 'string') return null;

    const v = value.trim();
    const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(v);
    if (!m) return null;

    let dd = Number(m[1]);
    const mm = Number(m[2]);
    const yyyy = Number(m[3]);

    if (mm === 0) return null;
    if (dd === 0) dd = 1;

    const d = new Date(yyyy, mm - 1, dd);

    // Prevent JS Date rollover (e.g. 32.01.2024 -> 01.02.2024)
    if (d.getFullYear() !== yyyy || d.getMonth() !== mm - 1 || d.getDate() !== dd) {
      return null;
    }

    return d;
  }

  /**
   * Subtracts months while clamping the day to the last day of the target month.
   * Example: 31.03 - 1 month => 28.02/29.02
   */
  subtractMonths(date: Date, months: number): Date {
    const y = date.getFullYear();
    const m = date.getMonth();
    const d = date.getDate();

    const targetMonthIndex = m - months;
    const targetYear = y + Math.floor(targetMonthIndex / 12);
    const targetMonth = ((targetMonthIndex % 12) + 12) % 12;

    const lastDay = new Date(targetYear, targetMonth + 1, 0).getDate();
    const targetDay = Math.min(d, lastDay);

    return new Date(targetYear, targetMonth, targetDay);
  }

  noZeroMonthValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const raw = control.value;
      if (raw == null || raw === '') return null;
      if (typeof raw !== 'string') return null;

      const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(raw.trim());
      if (!m) return null; // Format macht euer baseValidator
      return m[2] === '00' ? { monthIsZeroNotAllowed: true } : null;
    };
  }

}
